<template>
  <account-modal
    class="create-account-modal"
    scrollable-container
  >
    <template slot="heading">Requisites</template>

    <template slot="content">
      <one-data-column :label-props="{ style: 'display: none;' }">
        <v-layout row>
          <v-flex xs10>
            <account-select
              v-model="form.accountId"
              class="t-account-field"
              label="Select account"
              regular
              hide-balance
              :accounts="accounts"
              :loading="loading"
              :disabled="loading"
              @change="onAccountChange"
            />
          </v-flex>
        </v-layout>
      </one-data-column>

      <template v-if="!loading">
        <one-data-column :label-props="{ style: 'display: none;' }">
          <v-layout row>
            <v-flex xs10>
              <v-select
                v-model="form.currency"
                class="t-currency-field"
                label="Select currency"
                :items="currencies"
                regular
              />
            </v-flex>
          </v-layout>
        </one-data-column>

        <one-data-column v-if="canSelectType">
          <data-label slot="label">Type</data-label>
          <v-radio-group
            v-model="form.type"
            class="pt-0 mt-0 t-type-field"
            hide-details
          >
            <v-radio
              v-for="typeOption in typeOptions"
              :key="typeOption.value"
              color="primary"
              :value="typeOption.value"
            >
              <template slot="label">
                <img
                  v-if="typeIcons[typeOption.value]"
                  style="margin-right: 3px"
                  :src="typeIcons[typeOption.value]"
                  alt=""
                  width="18"
                  height="18"
                >
                <span>{{ typeOption.name }}</span>
              </template>
            </v-radio>
          </v-radio-group>
        </one-data-column>

        <one-data-column v-if="canSelectChannel">
          <data-label slot="label">Transfer details</data-label>
          <v-radio-group
            v-model="form.channel"
            class="pt-0 mt-0 t-channel-field"
            hide-details
          >
            <v-radio
              v-for="channelOption in channelOptions"
              :key="channelOption.value"
              color="primary"
              :label="channelOption.name"
              :value="channelOption.value"
            >
              <template slot="label">
                <img
                  v-if="bankIcons[channelOption.value]"
                  style="margin-right: 3px"
                  :src="bankIcons[channelOption.value]"
                  alt=""
                  width="18"
                  height="18"
                >
                <span>{{ channelOption.name }}</span>
              </template>
            </v-radio>
          </v-radio-group>
        </one-data-column>

        <one-data-column v-if="canSelectCountry">
          <data-label slot="label">Country</data-label>
          <v-radio-group
            v-model="form.country"
            class="pt-0 mt-0 t-country-field"
            hide-details
          >
            <v-radio
              v-for="countryOption in countryOptions"
              :key="countryOption.value"
              color="primary"
              :label="countryOption.name"
              :value="countryOption.value"
            />
          </v-radio-group>
        </one-data-column>

        <one-data-column v-if="hasSelectedRequisites && !detailsVisible">
          <v-btn
            class="t-show-details-button mr-0 ml-0"
            color="primary"
            flat
            small
            @click="onShowDetailsButtonClick"
          >
            <v-icon small>keyboard_arrow_down</v-icon>
            <span>show details</span>
          </v-btn>
        </one-data-column>

        <template v-if="detailsVisible">
          <requisites
            :requisites="selectedRequisites"
            :country="form.country"
          />

          <one-data-column>
            <v-btn
              class="t-hide-details-button mr-0 ml-0"
              color="primary"
              flat
              small
              @click="onHideDetailsButtonClick"
            >
              <v-icon small>keyboard_arrow_up</v-icon>
              <span>hide details</span>
            </v-btn>
          </one-data-column>
        </template>
      </template>
    </template>

    <template slot="actions">
      <v-btn
        class="t-download-button"
        color="primary"
        flat
        target="_blank"
        :href="downloadPdfUrl"
        :disabled="isDownloadButtonDisabled"
      >download pdf</v-btn>
      <v-btn

        class="t-send-button"
        color="primary"
        flat
        :disabled="isSendButtonDisabled"
        @click="onSendButtonClick"
      >send via e-mail</v-btn>

      <v-btn
        class="t-close-button"
        color="primary"
        flat="flat"
        @click="closeModal"
      >cancel</v-btn>
    </template>
  </account-modal>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex'
  import { AppConfig } from '@/config/config'

  // Services
  import apiClient from '@/services/AccountClient'
  import ApiRequest from '@/services/ApiRequests/ApiRequest'
  import AccountApi from '@/services/Api/AccountApi'

  // Models
  import Payment, { PAYMENT_CHANNELS } from '@/models/Payment'
  import RequisitesModel, { REQUISITES_TYPES } from '@/models/Requisites'

  // Mixins
  import form from '@/mixins/form'
  import countries from '@/mixins/countries'

  // Components
  import AccountModal from '@/components/accounts/AccountModal'
  import AccountSelect from '@/components/ui/AccountSelect'
  import DataLabel from '@/components/ui/Data/DataLabel'
  import OneDataColumn from '@/components/ui/Data/OneDataColumn'
  import Requisites from '@/components/accounts/Requisites'

  import SepaIcon from '@/assets/images/requisites/types/sepa.svg'
  import SwiftIcon from '@/assets/images/requisites/types/swift.svg'
  import FpsIcon from '@/assets/images/requisites/types/fps.svg'
  import BoIcon from '@/assets/images/requisites/banks/bo.svg'
  import IfxIcon from '@/assets/images/requisites/banks/ifx.svg'
  import AlfaBankIcon from '@/assets/images/requisites/banks/alfa-bank.svg'
  import CzcbBankIcon from '@/assets/images/requisites/banks/czcb-bank.svg'
  import IFastBankIcon from '@/assets/images/requisites/banks/ifast-bank.svg'
  import ManoBankIcon from '@/assets/images/requisites/banks/mano-bank.svg'
  import IncoreBankAgIcon from '@/assets/images/requisites/banks/incore-bank-ag.svg'
  import FvBankIcon from '@/assets/images/requisites/banks/fv-bank.svg'

  export const REQUISITES_COUNTRY_OTHER = 'OTHER'

  export default {
    name: 'RequisitesModal',
    components: {
      AccountModal,
      AccountSelect,
      DataLabel,
      OneDataColumn,
      Requisites
    },
    mixins: [form, countries],
    data () {
      return {
        detailsVisible: false,
        form: {
          accountId: null,
          currency: null,
          type: null,
          channel: null,
          country: null
        },
        requisitesRequestApiRequest: new ApiRequest(AccountApi.fetchRequisites)
      }
    },
    computed: {
      ...mapGetters('modal', ['modal']),
      profile () {
        return this.modal.data.profile
      },
      account () {
        return this.modal.data.account
      },
      accounts () {
        return this.modal.data.accounts
      },
      currencies () {
        return this.requisites
          .map(({ currency }) => currency)
          .filter((currency, index, currencies) => currencies.indexOf(currency) === index)
      },
      types () {
        return this.requisites
          .filter(({ currency }) => this.form.currency === currency)
          .map(({ type }) => type)
          .filter((type, index, types) => types.indexOf(type) === index)
      },
      typeIcons () {
        return {
          [REQUISITES_TYPES.SEPA]: SepaIcon,
          [REQUISITES_TYPES.SWIFT]: SwiftIcon,
          [REQUISITES_TYPES.FPS]: FpsIcon
        }
      },
      bankIcons () {
        return {
          [PAYMENT_CHANNELS.BLUOR_BANK]: BoIcon,
          [PAYMENT_CHANNELS.ALFA_BANK]: AlfaBankIcon,
          [PAYMENT_CHANNELS.IFX]: IfxIcon,
          [PAYMENT_CHANNELS.CZCB]: CzcbBankIcon,
          [PAYMENT_CHANNELS.IFAST]: IFastBankIcon,
          [PAYMENT_CHANNELS.MANO]: ManoBankIcon,
          [PAYMENT_CHANNELS.INCORE]: IncoreBankAgIcon,
          [PAYMENT_CHANNELS.FV_BANK]: FvBankIcon
        }
      },
      channels () {
        return this.requisites
          .filter(({ currency, type }) => this.form.currency === currency && this.form.type === type)
          .map(({ providerType }) => providerType)
          .filter((providerType, index, providerTypes) => providerTypes.indexOf(providerType) === index)
      },
      countries () {
        const requisites = this.requisites.find(({ currency, type, providerType, intermediateBank }) =>
          this.form.currency === currency &&
          this.form.type === type &&
          this.form.channel === providerType &&
          intermediateBank != null
        )

        return requisites != null ? Object.keys(requisites.intermediateBank) : []
      },
      typeOptions () {
        return RequisitesModel.getTypeOptions(this.form.currency).filter(({ value }) => this.types.includes(value))
      },
      channelOptions () {
        return Payment.channelOptions.filter(({ value }) => this.channels.includes(value))
      },
      countryOptions () {
        return this.countries.map(country => {
          const value = country

          if (country === REQUISITES_COUNTRY_OTHER) {
            return {
              name: 'Other countries',
              value
            }
          }

          return {
            name: this.getCountryName(country),
            value
          }
        })
      },
      selectedAccount () {
        return this.accounts.find(({ accountId }) => this.form.accountId === accountId)
      },
      canSelectType () {
        return this.form.currency !== null
      },
      canSelectChannel () {
        return this.form.type !== null
      },
      canSelectCountry () {
        return this.form.channel != null && this.countryOptions.length > 0
      },
      isDownloadButtonDisabled () {
        return !this.isFormFilled || this.loading
      },
      isSendButtonDisabled () {
        return !this.isFormFilled || this.loading
      },
      hasSelectedRequisites () {
        return this.selectedRequisites != null
      },
      requiredFields () {
        const requiredFields = [
          'accountId',
          'currency',
          'type',
          'channel'
        ]

        if (this.countries.length > 0) {
          requiredFields.push('country')
        }

        return requiredFields
      },
      requisites () {
        if (this.requisitesRequestApiRequest.data == null) {
          return []
        }

        return this.requisitesRequestApiRequest.data.map(
          requisitesData => new RequisitesModel(requisitesData)
        )
      },
      loading () {
        return this.requisitesRequestApiRequest.loading
      },
      selectedRequisites () {
        return this.requisites.find(({ type, currency, providerType, intermediateBank }) =>
          type === this.form.type &&
          currency === this.form.currency &&
          providerType === this.form.channel &&
          (intermediateBank == null || intermediateBank[this.form.country])
        )
      },
      downloadPdfUrl () {
        const { accountId, ...restForm } = this.form
        const query = apiClient.serializeParams(restForm)

        return `${AppConfig.GATEWAY_ACCOUNT_URL}/api/admin/v1/accounts/requisites/${accountId}/export?${query}`
      }
    },
    watch: {
      currencyOptions (currencyOptions) {
        if (currencyOptions.length === 1) {
          return this.setFieldValue('currency', currencyOptions[0])
        }

        this.setFieldValue('currency', null)
      },
      typeOptions (typeOptions) {
        if (typeOptions.length === 1) {
          return this.setFieldValue('type', this.typeOptions[0].value)
        }

        this.setFieldValue('type', null)
      },
      channelOptions (channelOptions) {
        if (channelOptions.length === 1) {
          return this.setFieldValue('channel', this.channelOptions[0].value)
        }

        this.setFieldValue('channel', null)
      },
      countryOptions (countryOptions) {
        if (countryOptions.length === 1) {
          return this.setFieldValue('country', this.countryOptions[0].value)
        }

        this.setFieldValue('country', null)
      },
      hasSelectedRequisites (hasSelectedRequisites) {
        if (!hasSelectedRequisites && this.setDetailsVisible) {
          this.setDetailsVisible(false)
        }
      }
    },
    created () {
      this.setFieldValue('accountId', this.account.accountId)
      this.loadRequisites()
    },
    methods: {
      ...mapActions('modal', ['showModal', 'closeModal']),
      onAccountChange () {
        this.loadRequisites()
      },
      onSendButtonClick () {
        this.showModal({
          name: 'RequisitesSendModal',
          canCloseOutside: false,
          size: '630px',
          data: {
            profileId: this.profile.id,
            ...this.form
          }
        })
      },
      onShowDetailsButtonClick () {
        this.setDetailsVisible(true)
      },
      onHideDetailsButtonClick () {
        this.setDetailsVisible(false)
      },
      setDetailsVisible (detailsVisible) {
        this.$set(this, 'detailsVisible', detailsVisible)
      },
      loadRequisites () {
        this.requisitesRequestApiRequest.execute(this.form.accountId)
          .catch(() => {})
      }
    }
  }
</script>
